
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdminManagement } from '../hooks/useAdminManagement';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Trash2, UserPlus, Shield, ShieldCheck, Coins, Briefcase, Gift, BarChart3, MapPin, Crown, ShieldAlert } from 'lucide-react';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CreditManagementPanel } from './Admin/CreditManagementPanel';
import AdminProvidersPanel from './Admin/AdminProvidersPanel';
import { AdminReferralsPanel } from './admin/AdminReferralsPanel';
import AdminGeoPromotions from './admin/AdminGeoPromotions';
import AdminAmbassadorsPanel from './admin/AdminAmbassadorsPanel';
import AdminPlacesPanel from './admin/AdminPlacesPanel';

const AdminManagementPanel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { 
    adminUsers, 
    isLoading, 
    isAdmin, 
    addAdmin, 
    updateAdmin, 
    removeAdmin,
    isAdding,
    isUpdating,
    isRemoving 
  } = useAdminManagement();
  
  const [newAdminUserId, setNewAdminUserId] = useState('');
  const [newAdminRole, setNewAdminRole] = useState<'admin' | 'moderator'>('moderator');
  const [showAddDialog, setShowAddDialog] = useState(false);

  if (!isAdmin) {
    return (
      <div className="container mx-auto max-w-4xl p-4">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <Shield className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('adminPanel.accessDenied')}</h3>
              <p className="text-muted-foreground">
                {t('adminPanel.onlyAdminsCanAccess')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleAddAdmin = () => {
    if (!newAdminUserId.trim()) {
      toast.error(t('adminPanel.adminManagement.pleaseEnterUserId'));
      return;
    }

    addAdmin({ userId: newAdminUserId, role: newAdminRole });
    const roleLabel = newAdminRole === 'admin' ? t('adminPanel.adminManagement.roleAdmin') : t('adminPanel.adminManagement.roleModerator');
    toast.success(t('adminPanel.adminManagement.adminAddedSuccess', { role: roleLabel }));
    setNewAdminUserId('');
    setNewAdminRole('moderator');
    setShowAddDialog(false);
  };

  const handleUpdateRole = (adminId: string, newRole: 'admin' | 'moderator') => {
    updateAdmin({ adminId, role: newRole });
    toast.success(t('adminPanel.adminManagement.roleUpdatedSuccess', { role: newRole }));
  };

  const handleRemoveAdmin = (adminId: string, userName?: string) => {
    const name = userName || t('adminPanel.adminManagement.unknownUser');
    if (confirm(t('adminPanel.adminManagement.confirmRemove', { name }))) {
      removeAdmin(adminId);
      toast.success(t('adminPanel.adminManagement.removedSuccess'));
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <Badge variant="destructive" className="flex items-center gap-1">
          <ShieldCheck className="h-3 w-3" />
          {t('adminPanel.adminRoles.admin')}
        </Badge>;
      case 'moderator':
        return <Badge variant="secondary" className="flex items-center gap-1">
          <Shield className="h-3 w-3" />
          {t('adminPanel.adminRoles.moderator')}
        </Badge>;
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl p-4 space-y-4">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-8 w-24" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl p-4">
      <Tabs defaultValue="admins" className="w-full">
        <TabsList className="grid w-full grid-cols-4 sm:grid-cols-8 gap-1 h-auto p-1">
          <TabsTrigger value="admins" className="flex items-center justify-center gap-1 text-[10px] sm:text-sm px-1 sm:px-3 py-2">
            <ShieldCheck className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
            <span className="truncate">{t('adminPanel.tabs.users')}</span>
          </TabsTrigger>
          <TabsTrigger value="credits" className="flex items-center justify-center gap-1 text-[10px] sm:text-sm px-1 sm:px-3 py-2">
            <Coins className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
            <span className="truncate">{t('adminPanel.tabs.credits')}</span>
          </TabsTrigger>
          <TabsTrigger value="providers" className="flex items-center justify-center gap-1 text-[10px] sm:text-sm px-1 sm:px-3 py-2">
            <Briefcase className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
            <span className="truncate">{t('adminPanel.tabs.providers')}</span>
          </TabsTrigger>
          <TabsTrigger value="referrals" className="flex items-center justify-center gap-1 text-[10px] sm:text-sm px-1 sm:px-3 py-2">
            <Gift className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
            <span className="truncate">{t('adminPanel.tabs.referrals')}</span>
          </TabsTrigger>
          <TabsTrigger value="ambassadors" className="flex items-center justify-center gap-1 text-[10px] sm:text-sm px-1 sm:px-3 py-2">
            <Crown className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
            <span className="truncate">{t('adminPanel.tabs.ambassadors', 'Ambassadors')}</span>
          </TabsTrigger>
          <TabsTrigger value="nutrition" className="flex items-center justify-center gap-1 text-[10px] sm:text-sm px-1 sm:px-3 py-2">
            <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
            <span className="truncate">{t('adminPanel.tabs.nutrition')}</span>
          </TabsTrigger>
          <TabsTrigger value="geo-promotions" className="flex items-center justify-center gap-1 text-[10px] sm:text-sm px-1 sm:px-3 py-2">
            <MapPin className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
            <span className="truncate">{t('adminPanel.tabs.geoPromotions', 'Geo Promos')}</span>
          </TabsTrigger>
          <TabsTrigger value="places" className="flex items-center justify-center gap-1 text-[10px] sm:text-sm px-1 sm:px-3 py-2">
            <ShieldAlert className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
            <span className="truncate">{t('adminPanel.tabs.places', 'Places')}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="admins">
          <Card>
        <CardHeader className="p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5" />
                  {t('adminPanel.adminManagement.title')}
                  <Badge variant="outline" className="text-xs">{t('adminPanel.adminManagement.usersCount', { count: adminUsers.length })}</Badge>
                </CardTitle>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="w-full sm:w-auto whitespace-nowrap">
                  <UserPlus className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="text-xs sm:text-sm">{t('adminPanel.adminManagement.addAdmin')}</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('adminPanel.adminManagement.addNewTitle')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">{t('adminPanel.adminManagement.userIdLabel')}</label>
                    <Input
                      value={newAdminUserId}
                      onChange={(e) => setNewAdminUserId(e.target.value)}
                      placeholder={t('adminPanel.adminManagement.userIdPlaceholder')}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('adminPanel.adminManagement.roleLabel')}</label>
                    <Select value={newAdminRole} onValueChange={(value: 'admin' | 'moderator') => setNewAdminRole(value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="moderator">{t('adminPanel.adminManagement.roleModerator')}</SelectItem>
                        <SelectItem value="admin">{t('adminPanel.adminManagement.roleAdmin')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={handleAddAdmin}
                      disabled={isAdding}
                      className="flex-1"
                    >
                      {isAdding ? t('adminPanel.adminManagement.adding') : t('adminPanel.adminManagement.addUser')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowAddDialog(false)}
                      className="flex-1"
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {adminUsers.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('adminPanel.adminManagement.noAdministrators')}</h3>
              <p className="text-muted-foreground">
                {t('adminPanel.adminManagement.noAdministratorsDesc')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {adminUsers.map((admin) => (
                <Card key={admin.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-3 sm:pt-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-sm sm:text-base">
                            {admin.user_name || t('adminPanel.adminManagement.unknownUser')}
                          </h4>
                          {getRoleBadge(admin.role)}
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">
                          {t('adminPanel.adminManagement.emailLabel')}: {admin.user_email || 'N/A'}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">
                          {t('adminPanel.adminManagement.idLabel')}: {admin.user_id}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {t('adminPanel.adminManagement.addedLabel')}: {format(new Date(admin.created_at), 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <Select
                          value={admin.role}
                          onValueChange={(value: 'admin' | 'moderator') => 
                            handleUpdateRole(admin.id, value)
                          }
                          disabled={isUpdating}
                        >
                          <SelectTrigger className="flex-1 sm:w-32 text-xs sm:text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="moderator">{t('adminPanel.adminManagement.roleModerator')}</SelectItem>
                            <SelectItem value="admin">{t('adminPanel.adminManagement.roleAdmin')}</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveAdmin(admin.id, admin.user_name || undefined)}
                          disabled={isRemoving}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="credits">
          <CreditManagementPanel />
        </TabsContent>

        <TabsContent value="providers">
          <AdminProvidersPanel />
        </TabsContent>

        <TabsContent value="referrals">
          <AdminReferralsPanel />
        </TabsContent>

        <TabsContent value="ambassadors">
          <AdminAmbassadorsPanel />
        </TabsContent>

        <TabsContent value="nutrition">
          <Card>
            <CardContent className="py-8 text-center">
              <BarChart3 className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('nutritionAdmin.panelTitle')}</h3>
              <p className="text-muted-foreground mb-4">
                {t('nutritionAdmin.panelDescription')}
              </p>
              <Button onClick={() => navigate('/admin/nutrition')}>
                {t('nutritionAdmin.accessPanel')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="geo-promotions">
          <AdminGeoPromotions />
        </TabsContent>

        <TabsContent value="places">
          <AdminPlacesPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminManagementPanel;
